import { Group, Loader, Modal, Stack, Text, Title, Tooltip } from '@mantine/core';
import { IconAlertTriangle, IconX } from '@tabler/icons-react';
import { type CSSProperties, type ReactNode } from 'react';

export interface ModalEditorProps {
    opened?: boolean;
    onClose: () => void;
    size?: string | number;
    children?: ReactNode;
    closeOnClickOutside?: boolean;
    withCloseButton?: boolean;
    title?: string;
    description?: string;
    loading?: boolean;
    error?: string | null;
    /** Stack ID for Modal.Stack integration - required when using Modal.Stack */
    stackId?: string;
    centered?: boolean;
    /**
     * Pins the modal to a fixed height (e.g. `80dvh`) instead of letting it grow with its content,
     * and turns the body into a flex column so a child with `flex={1} mih={0}` fills the leftover space.
     */
    height?: CSSProperties['height'];
}

export const ModalEditor = ({
    opened = true,
    onClose,
    size = 'md',
    children,
    closeOnClickOutside = true,
    withCloseButton,
    title,
    description,
    loading = false,
    stackId,
    centered = true,
    error,
    height,
}: ModalEditorProps) => (
    <Modal
        centered={centered}
        opened={opened}
        onClose={onClose}
        size={size}
        closeOnClickOutside={closeOnClickOutside}
        withCloseButton={withCloseButton}
        closeOnEscape={true}
        closeButtonProps={{ icon: <IconX size={18} /> }}
        stackId={stackId}
        styles={{
            content: {
                border: '1px solid var(--mantine-color-default-border)',
                ...(height && {
                    height,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }),
            },
            body: height ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : undefined,
        }}
        title={
            title ? (
                <Group gap={'lg'}>
                    <Stack gap={6}>
                        <Title order={4} fw={500}>
                            {title}
                        </Title>
                        {description && (
                            <Text fz={14} c={'dimmed'}>
                                {description}
                            </Text>
                        )}
                    </Stack>
                    {loading && <Loader size={'xs'} color="var(--mantine-color-text)" />}
                    {error && (
                        <Tooltip label={error} multiline style={{ whiteSpace: 'pre-line' }}>
                            <IconAlertTriangle size={24} color="red" />
                        </Tooltip>
                    )}
                </Group>
            ) : undefined
        }
    >
        {children}
    </Modal>
);
